import React from "react";
import { useNavigate } from "react-router";
import { NavLink } from "react-router-dom";
import { ROLES, normalizeRole } from "../../utils/roles";
import useAuth from "../../hooks/useAuth";
import useSidebarMenu from "../../hooks/useSidebarMenu";
import logger from "../../utils/logger";
import "./Sidebar.css";
import UserNavlinks from "./UserNavlinks";
import queryClient from "../../utils/queryClient";
import LogoutIcon from "@mui/icons-material/Logout";
import API from "../../utils/Axios.utils";

/**
 * Sidebar links for the signed-in role — managed in Intelliconvo, with the
 * built-in menus of utils/links.js as fallback (see useSidebarMenu).
 * Suppliers keep their existing sidebar layout; staff roles use the layout
 * with bottom-section links (e.g. Settings) under a divider.
 *
 * The wrapper's data-menu-source attribute ("intelliconvo" | "fallback")
 * shows in the browser's element inspector where the menu came from.
 */
const Navlinks = ({ toggleSidebar }) => {
  const navigate = useNavigate();
  const { role } = useAuth();
  const normalizedRole = normalizeRole(role);
  const { menu, source, isLoading } = useSidebarMenu();

  const handleLogout = async () => {
    logger.info(`[Navlinks] Logging out (role "${normalizedRole || "none"}").`);
    try {
      await API.post("/api/users/logout");
    } catch {
      // logout should never block navigation, even if the audit call fails
    }
    localStorage.clear();
    queryClient.clear();
    navigate("/login");
  };

  if (!isLoading && !menu.length) {
    logger.warn(`[Navlinks] No menu for role "${role || "none"}".`);
  }

  // Links appear once the menu has loaded; Logout is always available.
  const links = isLoading ? [] : menu;

  if (normalizedRole === ROLES.SUPPLIER) {
    return (
      <UserNavlinks
        NavLink={NavLink}
        userLinks={links}
        menuSource={source}
        toggleSidebar={toggleSidebar}
        handleLogout={handleLogout}
      />
    );
  }

  const topLinks = links.filter((link) => link.section !== "bottom");
  const bottomLinks = links.filter((link) => link.section === "bottom");
  const renderLink = ({ text, path, id, icon }) => (
    <NavLink
      to={path}
      key={id}
      className={({ isActive }) => (isActive ? "active nav-link" : "nav-link")}
      onClick={toggleSidebar}
    >
      <div className="icon">{icon}</div>
      {text}
    </NavLink>
  );

  return (
    <div className="nav-links" data-menu-source={source}>
      <div className="nav-top">{topLinks.map(renderLink)}</div>

      <div className="nav-bottom">
        <hr className="nav-divider" />
        {bottomLinks.map(renderLink)}
        <button
          style={{ background: "none", border: "none", cursor: "pointer" }}
          className="nav-link"
          onClick={handleLogout}
        >
          <div className="icon"><LogoutIcon /></div>
          Logout
        </button>
      </div>
    </div>
  );
};

export default Navlinks;
